import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { pretty } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import HubRiseChannel from "@/components/HubRiseChannel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Phone, CheckCircle2, Printer } from "lucide-react";
import { printReceipt } from "@/lib/receipt";
import { toast } from "sonner";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const PERIOD_GROUPS = [
  { key: "periods", label: "Opening" },
  { key: "takeaway_periods", label: "Takeaway" },
  { key: "booking_periods", label: "Booking" },
];

function toPeriods(row, key, openKey, closeKey) {
  if (Array.isArray(row[key]) && row[key].length) return row[key];
  if (row[openKey]) return [{ open: row[openKey], close: row[closeKey] }];
  return [{ open: "11:00", close: "22:00" }];
}

export default function Settings() {
  const { rid } = useParams();
  const [rest, setRest] = useState(null);
  const [settings, setSettings] = useState(null);
  const [hours, setHours] = useState([]);
  const [tables, setTables] = useState([]);
  const [phone, setPhone] = useState(null);
  const [sub, setSub] = useState(null);
  const [channels, setChannels] = useState({ channels: [], integrations: [], supported_integration_types: [] });
  const [newTable, setNewTable] = useState({ table_name: "", capacity: 2 });
  const [testPc, setTestPc] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState(null);
  const [subMsg, setSubMsg] = useState(null);
  const [stripeCfg, setStripeCfg] = useState({ enabled: false, has_key: false });
  const [stripeKey, setStripeKey] = useState("");

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}`).then((r) => setRest(r.data));
    api.get(`/restaurants/${rid}/settings`).then((r) => setSettings(r.data));
    api.get(`/restaurants/${rid}/opening-hours`).then((r) => setHours(DAYS.map((d) => {
      const row = r.data.find((h) => h.day_of_week === d) || { day_of_week: d, is_closed: false };
      return {
        day_of_week: d, is_closed: !!row.is_closed,
        periods: toPeriods(row, "periods", "open_time", "close_time"),
        takeaway_periods: toPeriods(row, "takeaway_periods", "takeaway_open_time", "takeaway_close_time"),
        booking_periods: toPeriods(row, "booking_periods", "booking_open_time", "booking_close_time"),
      };
    })));
    api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data));
    api.get(`/restaurants/${rid}/phone-setup`).then((r) => setPhone(r.data));
    api.get(`/restaurants/${rid}/subscription`).then((r) => setSub(r.data));
    api.get(`/restaurants/${rid}/channels`).then((r) => setChannels(r.data));
    api.get(`/plans`).then((r) => setPlans(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/features`).then((r) => setFeatures(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/stripe-config`).then((r) => setStripeCfg(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    setSubMsg("Checking payment…");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setSubMsg("Subscription activated."); load(); return; }
        if (data.status === "expired") { setSubMsg("Payment session expired."); return; }
      } catch {}
      if (attempts < 6) setTimeout(poll, 2000); else setSubMsg("Payment still processing — refresh shortly.");
    };
    poll();
    window.history.replaceState({}, "", `/r/${rid}/settings`);
  }, [rid, load]);

  useEffect(() => {
    const hb = new URLSearchParams(window.location.search).get("hubrise");
    if (hb === "connected") toast.success("HubRise connected! Push your menu to finish setup.");
    else if (hb === "error") toast.error("HubRise connection failed. Please try again.");
    if (hb) window.history.replaceState({}, "", `/r/${rid}/settings?tab=channels`);
  }, [rid]);

  const saveStripe = async () => {
    const body = { enabled: stripeCfg.enabled };
    if (stripeKey) body.secret_key = stripeKey;
    const { data } = await api.put(`/restaurants/${rid}/stripe-config`, body);
    setStripeCfg(data); setStripeKey("");
    toast.success("Payment settings saved");
  };

  const startSubscription = async (planId) => {
    try {
      const { data } = await api.post(`/payments/checkout/subscription`, { restaurant_id: rid, plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout"); }
  };

  const saveRest = async () => { await api.put(`/restaurants/${rid}`, rest); toast.success("Restaurant saved"); };
  const saveSettings = async () => { await api.put(`/restaurants/${rid}/settings`, settings); toast.success("AI settings saved"); };
  const saveHours = async () => { await api.put(`/restaurants/${rid}/opening-hours`, hours); toast.success("Opening hours saved"); };
  const updDay = (idx, patch) => setHours((p) => p.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const updPeriod = (idx, gkey, pidx, field, val) => setHours((p) => p.map((x, i) => i === idx
    ? { ...x, [gkey]: x[gkey].map((pp, j) => j === pidx ? { ...pp, [field]: val } : pp) } : x));
  const addPeriod = (idx, gkey) => setHours((p) => p.map((x, i) => i === idx ? { ...x, [gkey]: [...x[gkey], { open: "17:00", close: "22:00" }] } : x));
  const rmPeriod = (idx, gkey, pidx) => setHours((p) => p.map((x, i) => i === idx ? { ...x, [gkey]: x[gkey].filter((_, j) => j !== pidx) } : x));
  const savePhone = async () => { await api.put(`/restaurants/${rid}/phone-setup`, phone); toast.success("Phone setup saved"); };
  const addTable = async () => {
    if (!newTable.table_name) return;
    await api.post(`/restaurants/${rid}/tables`, { ...newTable, capacity: +newTable.capacity });
    setNewTable({ table_name: "", capacity: 2 }); load(); toast.success("Table added");
  };
  const delTable = async (id) => { await api.delete(`/tables/${id}`); load(); };
  const addChannel = async () => {
    await api.post(`/restaurants/${rid}/channels`, { channel_type: "JUST_EAT", name: "Just Eat" });
    load(); toast.success("Channel placeholder added");
  };
  const locateCenter = async () => {
    if (!rest.postcode) return toast.error("Set the restaurant postcode first (Restaurant tab)");
    try {
      const { data } = await api.get(`/restaurants/${rid}/geocode`, { params: { postcode: rest.postcode } });
      setSettings({ ...settings, delivery_center_lat: data.lat, delivery_center_lng: data.lng });
      toast.success("Located from postcode — remember to save");
    } catch { toast.error("Could not locate that postcode"); }
  };
  const testPostcode = async () => {
    if (!testPc.trim()) return;
    try { const { data } = await api.post(`/restaurants/${rid}/delivery/check`, { postcode: testPc }); setTestResult(data); }
    catch { toast.error("Check failed"); }
  };

  if (!rest || !settings) return <div className="text-muted-foreground">Loading…</div>;

  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get("tab") || "restaurant";

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your restaurant, AI receptionist, hours, tables and integrations." />
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="restaurant" data-testid="tab-restaurant">Restaurant</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours">Opening hours</TabsTrigger>
          <TabsTrigger value="tables" data-testid="tab-tables">Tables</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI settings</TabsTrigger>
          <TabsTrigger value="delivery" data-testid="tab-delivery">Delivery</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="printing" data-testid="tab-printing">Printing</TabsTrigger>
          <TabsTrigger value="phone" data-testid="tab-phone">Phone setup</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">Channels</TabsTrigger>
        </TabsList>

        {/* Restaurant */}
        <TabsContent value="restaurant" className="mt-4">
          <Card className="p-6 rounded-2xl grid sm:grid-cols-2 gap-4 max-w-3xl">
            <div><Label>Name</Label><Input className="h-11 mt-1.5" value={rest.name || ""} onChange={(e) => setRest({ ...rest, name: e.target.value })} /></div>
            <div><Label>Postcode</Label><Input className="h-11 mt-1.5" value={rest.postcode || ""} onChange={(e) => setRest({ ...rest, postcode: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input className="h-11 mt-1.5" value={rest.address || ""} onChange={(e) => setRest({ ...rest, address: e.target.value })} /></div>
            <div><Label>Public phone number</Label><Input className="h-11 mt-1.5" value={rest.public_phone_number || ""} onChange={(e) => setRest({ ...rest, public_phone_number: e.target.value })} /></div>
            <div><Label>Handover phone number</Label><Input className="h-11 mt-1.5" value={rest.handover_phone_number || ""} onChange={(e) => setRest({ ...rest, handover_phone_number: e.target.value })} data-testid="handover-number-input" /></div>
            <div className="sm:col-span-2"><Button className="h-11" onClick={saveRest} data-testid="save-restaurant">Save restaurant</Button></div>
          </Card>
        </TabsContent>

        {/* Hours */}
        <TabsContent value="hours" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-4xl">
            <p className="text-sm text-muted-foreground mb-4">Add multiple periods per day for split hours (e.g. open for lunch, close, then reopen for dinner). Times are 24-hour (HH:MM).</p>
            <div className="space-y-4">
              {hours.map((h, idx) => (
                <div key={h.day_of_week} className="rounded-xl border border-border p-4" data-testid={`hours-day-${h.day_of_week}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-28 capitalize font-semibold">{h.day_of_week}</span>
                    <Switch checked={!h.is_closed} data-testid={`hours-open-${h.day_of_week}`}
                      onCheckedChange={(v) => updDay(idx, { is_closed: !v })} />
                    <span className="text-sm text-muted-foreground">{h.is_closed ? "Closed all day" : "Open"}</span>
                  </div>
                  {!h.is_closed && (
                    <div className="grid md:grid-cols-3 gap-4">
                      {PERIOD_GROUPS.map((g) => (
                        <div key={g.key}>
                          <p className="text-xs font-semibold text-muted-foreground mb-1.5">{g.label} hours</p>
                          <div className="space-y-1.5">
                            {(h[g.key] || []).map((pp, pidx) => (
                              <div key={pidx} className="flex items-center gap-1.5">
                                <Input className="h-9 w-24 text-sm" type="time" value={pp.open || ""} data-testid={`hours-${h.day_of_week}-${g.key}-${pidx}-open`}
                                  onChange={(e) => updPeriod(idx, g.key, pidx, "open", e.target.value)} />
                                <span className="text-xs">–</span>
                                <Input className="h-9 w-24 text-sm" type="time" value={pp.close || ""} data-testid={`hours-${h.day_of_week}-${g.key}-${pidx}-close`}
                                  onChange={(e) => updPeriod(idx, g.key, pidx, "close", e.target.value)} />
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmPeriod(idx, g.key, pidx)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                              </div>
                            ))}
                            <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`hours-add-${h.day_of_week}-${g.key}`}
                              onClick={() => addPeriod(idx, g.key)}><Plus className="h-3 w-3" /> Add period</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Button className="h-11 mt-5" onClick={saveHours} data-testid="save-hours">Save opening hours</Button>
          </Card>
        </TabsContent>

        {/* Tables */}
        <TabsContent value="tables" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl">
            <div className="flex items-end gap-2 mb-4">
              <div><Label>Table name</Label><Input className="h-11 mt-1.5" value={newTable.table_name} onChange={(e) => setNewTable({ ...newTable, table_name: e.target.value })} data-testid="new-table-name" /></div>
              <div><Label>Capacity</Label><Input type="number" className="h-11 mt-1.5 w-24" value={newTable.capacity} onChange={(e) => setNewTable({ ...newTable, capacity: e.target.value })} /></div>
              <Button className="h-11 gap-1" onClick={addTable} data-testid="add-table-button"><Plus className="h-4 w-4" /> Add</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {tables.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span>{t.table_name} · {t.capacity} seats</span>
                  <Button size="icon" variant="ghost" onClick={() => delTable(t.id)} data-testid={`del-table-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl space-y-5">
            <div className="flex items-center justify-between rounded-xl border border-border p-4">
              <div>
                <p className="font-display font-semibold">AI receptionist</p>
                <p className="text-sm text-muted-foreground">Emergency switch — turn AI answering on or off.</p>
              </div>
              <Switch checked={rest.ai_enabled} data-testid="ai-enabled-switch"
                onCheckedChange={async (v) => { await api.patch(`/restaurants/${rid}/ai`, { ai_enabled: v }); setRest({ ...rest, ai_enabled: v }); toast.success(`AI ${v ? "enabled" : "disabled"}`); }} />
            </div>
            <div><Label>AI greeting</Label><Textarea className="mt-1.5" value={settings.ai_greeting || ""} onChange={(e) => setSettings({ ...settings, ai_greeting: e.target.value })} data-testid="ai-greeting" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>AI answer mode</Label>
                <Select value={rest.ai_answer_mode} onValueChange={(v) => setRest({ ...rest, ai_answer_mode: v })}>
                  <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["ALWAYS", "AFTER_HOURS_ONLY", "BUSY_ONLY", "DISABLED"].map((m) => <SelectItem key={m} value={m}>{pretty(m)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>AI voice</Label><Input className="h-11 mt-1.5" value={settings.ai_voice || ""} onChange={(e) => setSettings({ ...settings, ai_voice: e.target.value })} /></div>
            </div>
            <div>
              <Label>Custom AI instructions</Label>
              <Textarea className="mt-1.5 min-h-24" placeholder="Restaurant-specific rules for the AI, e.g. 'Always mention our loyalty card', 'We don't do deliveries'. Safety & allergen rules always apply."
                value={settings.ai_custom_instructions || ""} onChange={(e) => setSettings({ ...settings, ai_custom_instructions: e.target.value })} data-testid="ai-custom-instructions" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Approx. order prep time (minutes)</Label>
                <Input type="number" min={0} className="h-11 mt-1.5" value={settings.average_prep_minutes ?? 25}
                  onChange={(e) => setSettings({ ...settings, average_prep_minutes: +e.target.value })} data-testid="prep-minutes" />
                <p className="text-xs text-muted-foreground mt-1">The AI tells callers this after confirming their order.</p>
              </div>
            </div>
            <div className="rounded-xl border border-border p-4 space-y-3">
              <p className="text-sm font-semibold">Microphone sensitivity (voice detection)</p>
              <p className="text-xs text-muted-foreground">Higher threshold = less sensitive to background noise (AI won't stop for small sounds). Longer silence = waits longer before responding.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="text-xs">Threshold (0–1)</Label>
                  <Input type="number" step="0.05" min={0} max={1} className="h-11 mt-1.5" value={settings.vad_threshold ?? 0.6}
                    onChange={(e) => setSettings({ ...settings, vad_threshold: +e.target.value })} data-testid="vad-threshold" /></div>
                <div><Label className="text-xs">Silence before reply (ms)</Label>
                  <Input type="number" step="50" min={100} className="h-11 mt-1.5" value={settings.vad_silence_ms ?? 700}
                    onChange={(e) => setSettings({ ...settings, vad_silence_ms: +e.target.value })} data-testid="vad-silence" /></div>
                <div><Label className="text-xs">Lead-in padding (ms)</Label>
                  <Input type="number" step="50" min={0} className="h-11 mt-1.5" value={settings.vad_prefix_ms ?? 300}
                    onChange={(e) => setSettings({ ...settings, vad_prefix_ms: +e.target.value })} data-testid="vad-prefix" /></div>
              </div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={settings.table_booking_enabled} onCheckedChange={(v) => setSettings({ ...settings, table_booking_enabled: v })} /><span className="text-sm">Table bookings enabled</span></div>
            <div className="flex items-center gap-3"><Switch checked={!!settings.delivery_enabled} onCheckedChange={(v) => setSettings({ ...settings, delivery_enabled: v })} data-testid="delivery-enabled-switch" /><span className="text-sm">Delivery enabled (AI will offer delivery & take address + postcode)</span></div>
            <div className="flex items-center gap-3"><Switch checked={rest.allergen_mode_enabled} onCheckedChange={(v) => setRest({ ...rest, allergen_mode_enabled: v })} /><span className="text-sm">Allergen-aware mode</span></div>
            <div className="flex gap-2"><Button className="h-11" onClick={async () => { await saveRest(); await saveSettings(); }} data-testid="save-ai">Save AI settings</Button></div>
          </Card>
        </TabsContent>

        {/* Delivery geofence */}
        <TabsContent value="delivery" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl space-y-5">
            <div className="flex items-center gap-3">
              <Switch checked={!!settings.delivery_enabled} data-testid="delivery-enabled-switch-2"
                onCheckedChange={(v) => setSettings({ ...settings, delivery_enabled: v })} />
              <span className="text-sm">Delivery enabled</span>
            </div>
            <div>
              <p className="font-display font-semibold">Delivery geofence</p>
              <p className="text-sm text-muted-foreground">Set a centre point and radius. Orders are checked against this area using the customer's UK postcode.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Centre latitude</Label><Input className="h-11 mt-1.5" type="number" step="0.0001" value={settings.delivery_center_lat ?? ""} onChange={(e) => setSettings({ ...settings, delivery_center_lat: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="delivery-lat" /></div>
              <div><Label>Centre longitude</Label><Input className="h-11 mt-1.5" type="number" step="0.0001" value={settings.delivery_center_lng ?? ""} onChange={(e) => setSettings({ ...settings, delivery_center_lng: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="delivery-lng" /></div>
              <div><Label>Delivery radius (km)</Label><Input className="h-11 mt-1.5" type="number" step="0.5" value={settings.delivery_radius_km ?? 5} onChange={(e) => setSettings({ ...settings, delivery_radius_km: parseFloat(e.target.value) })} data-testid="delivery-radius" /></div>
              <div><Label>Delivery fee (£)</Label><Input className="h-11 mt-1.5" type="number" step="0.5" value={settings.delivery_fee ?? 0} onChange={(e) => setSettings({ ...settings, delivery_fee: parseFloat(e.target.value) })} data-testid="delivery-fee" /></div>
              <div><Label>Minimum order (£)</Label><Input className="h-11 mt-1.5" type="number" step="1" value={settings.delivery_min_order ?? 0} onChange={(e) => setSettings({ ...settings, delivery_min_order: parseFloat(e.target.value) })} data-testid="delivery-min-order" /></div>
              <div className="flex items-end"><Button variant="outline" className="h-11 w-full" onClick={locateCenter} data-testid="locate-postcode">Locate from restaurant postcode</Button></div>
            </div>
            {settings.delivery_center_lat != null && settings.delivery_center_lng != null && (
              <div className="rounded-xl overflow-hidden border border-border">
                <iframe title="delivery-map" className="w-full h-64" data-testid="delivery-map"
                  src={(() => {
                    const lat = settings.delivery_center_lat, lng = settings.delivery_center_lng;
                    const r = (settings.delivery_radius_km || 5);
                    const dLat = r / 111, dLng = r / (111 * Math.cos(lat * Math.PI / 180) || 1);
                    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - dLng},${lat - dLat},${lng + dLng},${lat + dLat}&marker=${lat},${lng}`;
                  })()} />
              </div>
            )}
            <div className="rounded-xl border border-border p-4">
              <p className="text-sm font-medium mb-2">Test a postcode</p>
              <div className="flex gap-2">
                <Input className="h-11" placeholder="e.g. SW1A 1AA" value={testPc} onChange={(e) => setTestPc(e.target.value)} data-testid="test-postcode-input" />
                <Button className="h-11" variant="outline" onClick={testPostcode} data-testid="test-postcode-btn">Check</Button>
              </div>
              {testResult && <p className="text-sm mt-2" data-testid="test-postcode-result">{testResult.message}</p>}
            </div>
            <Button className="h-11" onClick={saveSettings} data-testid="save-delivery">Save delivery settings</Button>
          </Card>
        </TabsContent>

        {/* Payments (per-restaurant Stripe) */}
        <TabsContent value="payments" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl space-y-5" data-testid="payments-card">
            <div>
              <p className="font-display font-semibold text-lg">Online card payments (Stripe)</p>
              <p className="text-sm text-muted-foreground">Connect your own Stripe account to take card payments for orders. Payments go directly to your Stripe. Find your secret key in Stripe Dashboard → Developers → API keys.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!stripeCfg.enabled} data-testid="stripe-enabled-switch"
                onCheckedChange={(v) => setStripeCfg({ ...stripeCfg, enabled: v })} />
              <span className="text-sm">Enable online card payments</span>
              {stripeCfg.has_key && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="stripe-key-badge"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Key saved</Badge>}
            </div>
            <div>
              <Label>Stripe Secret Key {stripeCfg.has_key && <span className="text-xs text-emerald-600">(saved — leave blank to keep)</span>}</Label>
              <Input type="password" className="h-11 mt-1.5" value={stripeKey} data-testid="stripe-secret-key"
                onChange={(e) => setStripeKey(e.target.value)} placeholder={stripeCfg.has_key ? "••••••••••••" : "sk_live_… or sk_test_…"} />
            </div>
            <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
              Payment rules: <b>Takeaway collection</b> — pay now or on collection · <b>Delivery</b> — payment required to place the order · <b>Dine-in & bookings</b> — place now, take payment later to close the order.
            </div>
            <Button className="h-11" onClick={saveStripe} data-testid="save-stripe">Save payment settings</Button>
          </Card>
        </TabsContent>

        {/* Printing */}
        <TabsContent value="printing" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl space-y-5" data-testid="printing-card">
            {(() => {
              const p = settings?.printing || {};
              const setP = (patch) => setSettings((s) => ({ ...s, printing: { ...(s.printing || {}), ...patch } }));
              const sampleOrder = {
                order_number: "ORD-SAMPLE", order_type: "TAKEAWAY", fulfillment_type: "COLLECTION",
                created_at: new Date().toISOString(), customer_name: "Test Customer", customer_phone: "07700 900000",
                declared_allergens: ["Milk"], allergy_dietary_note: "Severe dairy allergy",
                total: 12.5,
                items: [
                  { id: "1", quantity: 1, item_name_snapshot: "Garlic Bread", total_price: 4.5, options_snapshot: [], notes: "Extra crispy", allergens_snapshot: [{ name: "Milk" }, { name: "Cereals containing gluten" }] },
                  { id: "2", quantity: 2, item_name_snapshot: "Cola", total_price: 8.0, options_snapshot: [], allergens_snapshot: [] },
                ],
              };
              return (
                <>
                  <div>
                    <p className="font-display font-semibold text-lg">Thermal ticket printing</p>
                    <p className="text-sm text-muted-foreground">Prints kitchen tickets from the browser on your KDS device to its connected thermal printer. For fully automatic silent printing, run that device's Chrome in kiosk-printing mode with the thermal printer set as the default printer.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={!!p.enabled} data-testid="print-enabled" onCheckedChange={(v) => setP({ enabled: v })} />
                    <span className="text-sm">Enable ticket printing</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={!!p.auto_print} data-testid="print-auto" onCheckedChange={(v) => setP({ auto_print: v })} />
                    <span className="text-sm">Auto-print every new order on the Kitchen Display</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Paper width</Label>
                      <Select value={p.paper_width || "80"} onValueChange={(v) => setP({ paper_width: v })}>
                        <SelectTrigger className="h-11 mt-1.5" data-testid="print-paper"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="80">80mm (standard)</SelectItem>
                          <SelectItem value="58">58mm (compact)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Copies per order</Label>
                      <Input type="number" min={1} max={5} className="h-11 mt-1.5" value={p.copies ?? 1}
                        data-testid="print-copies" onChange={(e) => setP({ copies: Math.max(1, +e.target.value || 1) })} />
                    </div>
                    <div>
                      <Label>Preferred printer name (guidance)</Label>
                      <Input className="h-11 mt-1.5" value={p.printer_name || ""} placeholder="e.g. EPSON TM-T20 Receipt"
                        data-testid="print-printer-name" onChange={(e) => setP({ printer_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Receipt header</Label>
                      <Input className="h-11 mt-1.5" value={p.header ?? ""} placeholder={rest?.name || "Restaurant name"}
                        data-testid="print-header" onChange={(e) => setP({ header: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Receipt footer</Label>
                      <Input className="h-11 mt-1.5" value={p.footer ?? ""} placeholder="e.g. Thank you — see you soon!"
                        data-testid="print-footer" onChange={(e) => setP({ footer: e.target.value })} />
                    </div>
                  </div>
                  <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
                    Browsers can't list network printers directly, so the ticket prints to the KDS device's selected/default printer. If several printers are on the network, set the desired thermal printer as that device's default (or pick it once in the print dialog and Chrome will remember it). Multiple printers per device are supported by running one KDS tab per printer.
                  </div>
                  <div className="flex gap-2">
                    <Button className="h-11" onClick={saveSettings} data-testid="save-printing">Save printing settings</Button>
                    <Button variant="outline" className="h-11 gap-2" data-testid="test-print"
                      onClick={() => printReceipt(sampleOrder, settings?.printing || {}, rest || {})}><Printer className="h-4 w-4" /> Test print</Button>
                  </div>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        {/* Phone setup */}
        <TabsContent value="phone" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl">
            <div className="rounded-xl bg-secondary p-4 mb-5 text-sm space-y-1">
              <p className="flex items-center gap-2 font-medium"><Phone className="h-4 w-4" /> Call forwarding setup</p>
              <p className="text-muted-foreground">Forward your public number <b>{rest.public_phone_number || "—"}</b> to your hidden Twilio routing number <b>{rest.twilio_routing_number || "not assigned"}</b>. Twilio then streams audio to the AI receptionist over Media Streams (G.711 μ-law).</p>
            </div>
            <div className="space-y-2">
              {["twilio_number_assigned", "webhook_configured", "forwarding_enabled", "test_call_received", "ai_answered", "handover_tested", "sms_tested", "allergen_data_reviewed"].map((k) => (
                <div key={k} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span className="flex items-center gap-2">{phone?.[k] && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}{pretty(k)}</span>
                  <Switch checked={!!phone?.[k]} onCheckedChange={(v) => setPhone({ ...phone, [k]: v })} data-testid={`phone-check-${k}`} />
                </div>
              ))}
            </div>
            <Button className="h-11 mt-5" onClick={savePhone} data-testid="save-phone">Save checklist</Button>
          </Card>
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription" className="mt-4 space-y-4">
          <Card className="p-5 rounded-2xl">
            <p className="text-sm text-muted-foreground">Current plan</p>
            <p className="font-display text-2xl font-bold">{features?.plan?.name || rest.subscription_plan || "—"}</p>
            {rest.subscription_period_end && <p className="text-sm text-muted-foreground mt-1">Active until {new Date(rest.subscription_period_end).toLocaleDateString()}</p>}
            {features?.features && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {Object.entries(features.features).filter(([, v]) => v).map(([k]) => (
                  <Badge key={k} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{pretty(k)}</Badge>
                ))}
              </div>
            )}
            {subMsg && <p className="text-sm mt-3 text-primary" data-testid="sub-status-msg">{subMsg}</p>}
          </Card>

          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map((p) => {
              const current = features?.plan?.id === p.id;
              return (
                <Card key={p.id} data-testid={`plan-choice-${p.id}`} className={`p-5 rounded-2xl ${current ? "border-2 border-primary" : ""}`}>
                  <p className="font-display font-bold text-lg">{p.name}</p>
                  <p className="font-display text-2xl font-bold mt-1">£{p.price_monthly}<span className="text-sm text-muted-foreground font-normal">/mo</span></p>
                  <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                    {Object.entries(p.features || {}).filter(([, v]) => v).map(([k]) => <li key={k}>• {pretty(k)}</li>)}
                  </ul>
                  <Button className="w-full h-11 mt-4" variant={current ? "outline" : "default"} data-testid={`subscribe-${p.id}`}
                    onClick={() => startSubscription(p.id)}>{current ? "Renew (pay monthly)" : "Subscribe"}</Button>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">Note: payment activates the plan for 30 days (manual monthly renewal in this version).</p>
        </TabsContent>

        {/* Channels */}
        <TabsContent value="channels" className="mt-4">
          <Card className="p-6 rounded-2xl max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-display font-semibold">Order channels (future)</p>
                <p className="text-sm text-muted-foreground">Architecture ready for Just Eat & website orders. Integration types: {channels.supported_integration_types?.map(pretty).join(", ")}.</p>
              </div>
              <Button variant="outline" className="h-11 gap-1" onClick={addChannel} data-testid="add-channel"><Plus className="h-4 w-4" /> Add channel</Button>
            </div>
            <div className="space-y-2">
              {channels.channels.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span>{c.name} · {pretty(c.channel_type)}</span>
                  <Badge variant="outline" className={c.is_enabled ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}>{c.is_enabled ? "Enabled" : "Placeholder"}</Badge>
                </div>
              ))}
              {channels.channels.length === 0 && <p className="text-sm text-muted-foreground">No channels configured.</p>}
            </div>
          </Card>

          <Card className="p-6 rounded-2xl max-w-3xl mt-4" data-testid="deliverect-card">
            <div className="flex items-center gap-3 mb-2">
              <p className="font-display font-semibold">Deliverect (aggregator channels)</p>
              <Switch checked={!!settings.deliverect_enabled} data-testid="deliverect-enabled"
                onCheckedChange={(v) => setSettings({ ...settings, deliverect_enabled: v })} />
            </div>
            <p className="text-sm text-muted-foreground mb-4">Connect Just Eat, Uber Eats & Deliveroo via Deliverect. Orders flow straight into your kitchen. Platform credentials are configured by the operator; add your Deliverect account & location below.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Deliverect account ID</Label><Input className="h-11 mt-1.5" value={settings.deliverect_account_id || ""} onChange={(e) => setSettings({ ...settings, deliverect_account_id: e.target.value })} data-testid="deliverect-account" /></div>
              <div><Label>Deliverect location ID</Label><Input className="h-11 mt-1.5" value={settings.deliverect_location_id || ""} onChange={(e) => setSettings({ ...settings, deliverect_location_id: e.target.value })} data-testid="deliverect-location" /></div>
            </div>
            <div className="rounded-lg bg-secondary p-3 mt-3 text-xs text-muted-foreground">
              Inbound order webhook (give this to Deliverect): <code className="text-foreground">{process.env.REACT_APP_BACKEND_URL}/api/deliverect/orders</code>
            </div>
            <div className="flex gap-2 mt-4">
              <Button className="h-11" onClick={saveSettings} data-testid="save-deliverect">Save</Button>
              <Button variant="outline" className="h-11" data-testid="sync-menu-deliverect"
                onClick={async () => { const { data } = await api.post(`/restaurants/${rid}/deliverect/sync-menu`); data.ok ? toast.success(`Menu synced (${data.pushed_products} items)`) : toast.error(data.reason || "Sync unavailable"); }}>Sync menu to Deliverect</Button>
            </div>
          </Card>

          <HubRiseChannel rid={rid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
